import z from "zod";

import { ErrorClassification } from "../../../sentry/classification";
import type { ErrorDetails } from "../domain";

const dropboxSignErrorTypeValues = [
  "UNKNOWN",
  "NOT_IMPLEMENTED",
  "API_ERROR",
  "SIGNATURE_REQUEST_FAILED",
  "SIGN_URL_EXPIRED",
  "INVALID_STATE_TRANSITION",
  "DUPLICATE_ACTIVE_REQUESTS_DETECTED",
] as const;
const dropboxSignErrorTypeSchema = z.enum(dropboxSignErrorTypeValues);
const DropboxSignErrorType = dropboxSignErrorTypeSchema.enum;
type DropboxSignErrorType = z.infer<typeof dropboxSignErrorTypeSchema>;

export type DropboxSignErrors =
  | { errorType: typeof DropboxSignErrorType.UNKNOWN; data: undefined }
  | { errorType: typeof DropboxSignErrorType.NOT_IMPLEMENTED; data: undefined }
  | { errorType: typeof DropboxSignErrorType.API_ERROR; data: { message: string } }
  | { errorType: typeof DropboxSignErrorType.SIGNATURE_REQUEST_FAILED; data: { signatureRequestId?: string } }
  | { errorType: typeof DropboxSignErrorType.SIGN_URL_EXPIRED; data: { signerSignatureId: string } }
  | { errorType: typeof DropboxSignErrorType.INVALID_STATE_TRANSITION; data: { from: string; to: string } }
  | {
      errorType: typeof DropboxSignErrorType.DUPLICATE_ACTIVE_REQUESTS_DETECTED;
      data: { organizationId: string; documentType: string; activeRowIds: string[] };
    };

export const dropboxSignErrorDetails: Record<DropboxSignErrorType, ErrorDetails> = {
  UNKNOWN: { classification: ErrorClassification.UNEXPECTED_ERROR, sentryDescription: "Unknown Dropbox Sign error" },
  NOT_IMPLEMENTED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Dropbox Sign feature not yet implemented",
  },
  API_ERROR: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Dropbox Sign API error",
  },
  SIGNATURE_REQUEST_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Dropbox Sign signature request failed",
  },
  SIGN_URL_EXPIRED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Dropbox Sign embedded sign URL expired or is invalid",
  },
  INVALID_STATE_TRANSITION: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Dropbox Sign invalid state transition",
  },
  DUPLICATE_ACTIVE_REQUESTS_DETECTED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Two active Dropbox Sign requests detected for the same org+docType (likely double-click race)",
  },
};
